import { useState, useEffect, useRef, useCallback } from "react";
import { airports } from "@/data/airports";
import { Plane } from "lucide-react";

const searchAirportsData = (query) => {
  if (!query || query.length < 2) return [];
  const searchTerm = query.toLowerCase().trim();
  const searchUpper = query.toUpperCase().trim();
  const exactIataMatches = airports.filter(a => a.code === searchUpper);
  const partialMatches = airports.filter(airport => {
    if (airport.code === searchUpper) return false;
    return (
      airport.code.toLowerCase().includes(searchTerm) ||
      airport.city.toLowerCase().includes(searchTerm) ||
      airport.name.toLowerCase().includes(searchTerm) ||
      airport.country.toLowerCase().includes(searchTerm)
    );
  });
  return [...exactIataMatches, ...partialMatches].slice(0, 8);
};

export const AirportAutocomplete = ({
  value,
  onChange,
  onSelect,
  placeholder = "City or airport",
  id,
  "data-testid": testId,
  wrapperClassName = "",
  inputClassName = "",
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (value !== inputValue && value !== undefined) setInputValue(value);
  }, [value]);

  const handleSearch = useCallback((query) => {
    const results = searchAirportsData(query);
    setSuggestions(results);
    setIsOpen(results.length > 0);
    setHighlightedIndex(-1);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(newValue), 300);
  };

  const handleSelect = (airport) => {
    const displayValue = `${airport.city} (${airport.code})`;
    setInputValue(displayValue);
    onChange?.(displayValue);
    onSelect?.(airport);
    setIsOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => prev < suggestions.length - 1 ? prev + 1 : 0);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1);
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0) handleSelect(suggestions[highlightedIndex]);
        break;
      case "Escape":
        setIsOpen(false);
        break;
    }
  };

  const highlightMatch = (text, query) => {
    if (!query || query.length < 2) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <span key={i} className="font-semibold text-[#21b3ed]">{part}</span> : <span key={i}>{part}</span>
    );
  };

  return (
    <div ref={wrapperRef} className={`relative ${wrapperClassName}`}>
      <input
        ref={inputRef}
        id={id}
        data-testid={testId}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => { if (suggestions.length > 0) setIsOpen(true); }}
        placeholder={placeholder}
        autoComplete="off"
        className={inputClassName || "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 outline-none focus:border-[#090e4c] transition-colors bg-white"}
      />

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-[9999] w-full min-w-[280px] mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-72 overflow-y-auto">
          {suggestions.map((airport, index) => (
            <button
              key={`${airport.code}-${index}`}
              type="button"
              className={`w-full px-4 py-3 text-left flex items-start gap-3 transition-colors ${index === highlightedIndex ? "bg-blue-50" : "hover:bg-gray-50"}`}
              onClick={() => handleSelect(airport)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <Plane className="h-4 w-4 text-[#21b3ed] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">
                  {highlightMatch(airport.city, inputValue)}, {airport.country}
                  <span className="text-[#21b3ed] font-bold ml-2">({airport.code})</span>
                </div>
                <div className="text-xs text-gray-400 truncate">
                  {highlightMatch(airport.name, inputValue)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
